'use client'

import { useQuery } from '@tanstack/react-query'

import { useAuth } from '@/features/auth/hooks'
import { fetchChartData, fetchServerTime, fetchVisibleRangeProfile } from '@/service/chart.api'
import { ChartDataRequest, VisibleRangeProfileRequest } from '@/types/generated/public_api'

// Query keys
export const chartKeys = {
  all: (userId?: string | null) => ['chart', userId] as const,
  orderHistory: (userId?: string | null, filters?: any) =>
    [...chartKeys.all(userId), 'orderHistory', filters] as const,
  chartData: (userId?: string | null, payload?: ChartDataRequest) =>
    [...chartKeys.all(userId), 'chartData', payload] as const,
  serverTime: (userId?: string | null) => [...chartKeys.all(userId), 'serverTime'] as const,
  visibleRangeProfile: (userId?: string | null, payload?: VisibleRangeProfileRequest | null) =>
    [...chartKeys.all(userId), 'visibleRangeProfile', payload] as const,
}

export const useVisibleRangeProfile = (
  payload: VisibleRangeProfileRequest | null,
  enabled = true
) => {
  const { user } = useAuth()
  return useQuery({
    queryKey: chartKeys.visibleRangeProfile(user?.userId, payload),
    queryFn: ({ signal }) => {
      if (!payload) throw new Error('Visible range profile payload is required')
      return fetchVisibleRangeProfile(payload, signal)
    },
    enabled: enabled && payload !== null,
    staleTime: 10 * 1000,
    retry: false,
  })
}

// Chart data query
export const useChartData = (payload: ChartDataRequest) => {
  const { user } = useAuth()
  return useQuery({
    queryKey: chartKeys.chartData(user?.userId, payload),
    queryFn: () => fetchChartData(payload),
    enabled: !!payload.symbol && !!payload.exchange && !!payload.timeframe,
    staleTime: 10 * 1000, // 10 seconds
  })
}

// Server time query
export const useServerTime = () => {
  const { user } = useAuth()
  return useQuery({
    queryKey: chartKeys.serverTime(user?.userId),
    queryFn: () => fetchServerTime(),
    staleTime: 60 * 1000 * 60, // 1 hour
    refetchInterval: 60 * 1000 * 60, // Auto-refetch every hour
    refetchOnWindowFocus: 'always',
  })
}
